import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"

import FileUploaderV2 from "components/FileUploaderV2"
import MuiAutoComplete from "components/MuiAutocomplete"
import MuiAsyncAutoComplete from "components/MuiAsyncAutocomplete"
import MuiTextField from "components/MuiTextField"
import { ProjectFormPageProps } from "components/ProjectForm"
import usePermissions from "permissions/usePermissions"
import ProjectFormFooter from "../../../components/ProjectFormFooter"
import PaymentTerms from "../../PaymentDetails/PaymentTerms"
import PurchaseOrderItemsTable from "../PurchaseOrderItemsTable"
import useProjectBasicDetails, { EProjectBasicDetailsFields, EPurchaseOrderFields } from "./useProjectBasicDetails"

const ProjectBasicDetails = (props: ProjectFormPageProps) => {
    const permissions = usePermissions()
    const { states, functions } = useProjectBasicDetails(props)
    return (
        <>
            <Box
                display={"flex"}
                flexDirection={"column"}
                gap={4}
                className="custom-scrollbar"
                maxHeight={"65vh"}
                padding={"var(--mui-modal-padding)"}
                sx={{
                    overflowY: "auto",
                }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Project Name*</Typography>
                            <MuiTextField
                                name={EProjectBasicDetailsFields.PROJECT_NAME}
                                placeholder="Enter Project Name"
                                size="large"
                                value={states.formik.values[EProjectBasicDetailsFields.PROJECT_NAME]}
                                onChange={states.formik.handleChange}
                                onBlur={states.formik.handleBlur}
                                error={functions.hasError(EProjectBasicDetailsFields.PROJECT_NAME)}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="label">Deal Done by (Business POC)*</Typography>
                                <Typography
                                    variant="label"
                                    fontSize={"10px"}
                                    textTransform={"none"}
                                    onClick={functions.handleAssignToMe}
                                    sx={{
                                        textDecoration: "underline",
                                        textUnderlineOffset: "2px",
                                        fontWeight: "400",
                                        color: "#0430C0",
                                        cursor: "pointer",
                                    }}>
                                    Assign to me
                                </Typography>
                            </Box>
                            <MuiAutoComplete
                                disablePortal={false}
                                disableClearable
                                size="large"
                                placeholder="Select Name"
                                options={states.dealDoneByUsersOptions}
                                loading={states.isDealDoneByUsersFetching}
                                value={states.formik.values[EProjectBasicDetailsFields.DEAL_DONE_BY] || null}
                                onChange={value =>
                                    states.formik.setFieldValue(EProjectBasicDetailsFields.DEAL_DONE_BY, value)
                                }
                                error={functions.hasError(EProjectBasicDetailsFields.DEAL_DONE_BY)}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Expected Project Closure Date*</Typography>
                            <MuiTextField
                                name={EProjectBasicDetailsFields.PROJECT_CLOSURE_DATE}
                                placeholder="Select date"
                                type="date"
                                size="large"
                                value={states.formik.values[EProjectBasicDetailsFields.PROJECT_CLOSURE_DATE]}
                                onChange={states.formik.handleChange}
                                onBlur={states.formik.handleBlur}
                                error={functions.hasError(EProjectBasicDetailsFields.PROJECT_CLOSURE_DATE)}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Project Category*</Typography>
                            <MuiAutoComplete
                                size="large"
                                options={states.categoryOptions}
                                placeholder="Select Project Category"
                                loading={states.isCategoriesFetching}
                                value={states.formik.values[EProjectBasicDetailsFields.CATEGORY] || null}
                                onChange={value =>
                                    states.formik.setFieldValue(EProjectBasicDetailsFields.CATEGORY, value)
                                }
                                error={functions.hasError(EProjectBasicDetailsFields.CATEGORY)}
                            />
                        </Box>
                    </Grid>
                    <Grid size={6}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="label">Program</Typography>
                            <MuiAutoComplete
                                disablePortal={false}
                                size="large"
                                options={states.programsOptions}
                                placeholder="Select Program"
                                loading={states.programsLoading}
                                disabled={states.programsLoading}
                                value={states.formik.values[EProjectBasicDetailsFields.PROGRAM] || null}
                                onChange={value =>
                                    states.formik.setFieldValue(EProjectBasicDetailsFields.PROGRAM, value)
                                }
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="label">Sales Order{states.requireSalesOrder ? "*" : ""}</Typography>
                                {!states.showPurchaseOrderForm && permissions.PROJECTS__ADD_PO && (
                                    <Typography
                                        variant="label"
                                        onClick={functions.openPurchaseOrderForm}
                                        sx={{
                                            textDecoration: "underline",
                                            textUnderlineOffset: "2px",
                                            fontWeight: "400",
                                            color: "#0430C0",
                                            cursor: "pointer",
                                        }}>
                                        + Upload New
                                    </Typography>
                                )}
                            </Box>
                            {states.buyerAccountName && (
                                <Typography sx={{ fontSize: "10px" }} color="#70758D">
                                    Buyer account: {states.buyerAccountName}
                                </Typography>
                            )}
                            <MuiAutoComplete
                                disablePortal={false}
                                size="large"
                                options={states.purchaseOrderOptions}
                                placeholder="Select Sales Order"
                                loading={states.isLoadingPurchaseOrder}
                                disabled={states.showPurchaseOrderForm || states.isLoadingPurchaseOrder}
                                value={states.formik.values[EProjectBasicDetailsFields.PURCHASE_ORDER] || null}
                                onChange={value =>
                                    states.formik.setFieldValue(EProjectBasicDetailsFields.PURCHASE_ORDER, value)
                                }
                                error={functions.hasError(EProjectBasicDetailsFields.PURCHASE_ORDER)}
                            />
                            {!states.showPurchaseOrderForm && (
                                <Typography sx={{ fontSize: "10px" }} color="#70758D">
                                    {states.selectedPurchaseOrder
                                        ? `Client PO: ${states.selectedPurchaseOrder.po_number} · Order value ₹${Number(states.selectedPurchaseOrder.po_amount).toLocaleString("en-IN")} (incl. GST)`
                                        : "Select the Sales Order for this project."}
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                </Grid>

                {states.showPurchaseOrderForm && (
                    <Grid container spacing={2} bgcolor="#FAFAFA" padding={"12px"} borderRadius={"8px"}>
                        <Grid size={12} fontSize={"16px"} fontWeight={"500"}>
                            NEW PO
                            <Typography
                                variant="label"
                                onClick={functions.closePurchaseOrderForm}
                                fontSize={"12px"}
                                sx={{
                                    textDecoration: "underline",
                                    textUnderlineOffset: "2px",
                                    fontWeight: "400",
                                    color: "statiqOrange.main",
                                    cursor: "pointer",
                                    marginLeft: "8px",
                                }}>
                                REMOVE
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography variant="label">PO Name*</Typography>
                                <MuiTextField
                                    name={EPurchaseOrderFields.PO_NAME}
                                    placeholder="Enter PO Name"
                                    size="large"
                                    value={states.purchaseOrderFormik.values[EPurchaseOrderFields.PO_NAME]}
                                    onChange={states.purchaseOrderFormik.handleChange}
                                    onBlur={states.purchaseOrderFormik.handleBlur}
                                    error={functions.hasPurchaseOrderError(EPurchaseOrderFields.PO_NAME)}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography variant="label">PO Client Number*</Typography>
                                <MuiTextField
                                    name={EPurchaseOrderFields.PO_NUMBER}
                                    placeholder="Enter PO Client Number"
                                    size="large"
                                    value={states.purchaseOrderFormik.values[EPurchaseOrderFields.PO_NUMBER]}
                                    onChange={states.purchaseOrderFormik.handleChange}
                                    onBlur={states.purchaseOrderFormik.handleBlur}
                                    error={functions.hasPurchaseOrderError(EPurchaseOrderFields.PO_NUMBER)}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography variant="label">Select Account*</Typography>
                                <MuiAsyncAutoComplete
                                    disablePortal={false}
                                    disableClearable
                                    size="large"
                                    placeholder="Select Account"
                                    trigger={states.accountsTrigger}
                                    buildQueryArg={({ page, page_size, search }) => ({
                                        page,
                                        page_size,
                                        search_key: search,
                                    })}
                                    selectData={res => ({
                                        options: res.data.map((a: any) => ({ id: a.id, title: a.account_name })),
                                        hasMore: res.have_more,
                                    })}
                                    value={states.purchaseOrderFormik.values[EPurchaseOrderFields.ACCOUNT]}
                                    onChange={value =>
                                        functions.handleAccountChange(value as { id: number; title: string } | null)
                                    }
                                    error={functions.hasPurchaseOrderError(EPurchaseOrderFields.ACCOUNT)}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography variant="label">Select Billing Address*</Typography>
                                <MuiAutoComplete
                                    disablePortal={false}
                                    disableClearable
                                    size="large"
                                    placeholder="Select Billing Address"
                                    options={states.billingAddressOptions}
                                    value={states.purchaseOrderFormik.values[EPurchaseOrderFields.BILLING_ADDRESS]}
                                    onChange={value =>
                                        states.purchaseOrderFormik.setFieldValue(
                                            EPurchaseOrderFields.BILLING_ADDRESS,
                                            value
                                        )
                                    }
                                    loading={states.isFetchingBillingAddresses}
                                    disabled={
                                        !states.purchaseOrderFormik.values[EPurchaseOrderFields.ACCOUNT] ||
                                        states.isFetchingBillingAddresses
                                    }
                                    error={functions.hasPurchaseOrderError(EPurchaseOrderFields.BILLING_ADDRESS)}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography variant="label">PO Date*</Typography>
                                <MuiTextField
                                    name={EPurchaseOrderFields.PO_DATE}
                                    placeholder="Select date"
                                    type="date"
                                    size="large"
                                    value={states.purchaseOrderFormik.values[EPurchaseOrderFields.PO_DATE]}
                                    onChange={states.purchaseOrderFormik.handleChange}
                                    onBlur={states.purchaseOrderFormik.handleBlur}
                                    error={functions.hasPurchaseOrderError(EPurchaseOrderFields.PO_DATE)}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography variant="label">PO Receive Date*</Typography>
                                <MuiTextField
                                    name={EPurchaseOrderFields.PO_RECEIVE_DATE}
                                    placeholder="Select date"
                                    type="date"
                                    size="large"
                                    value={states.purchaseOrderFormik.values[EPurchaseOrderFields.PO_RECEIVE_DATE]}
                                    onChange={states.purchaseOrderFormik.handleChange}
                                    onBlur={states.purchaseOrderFormik.handleBlur}
                                    error={functions.hasPurchaseOrderError(EPurchaseOrderFields.PO_RECEIVE_DATE)}
                                    slotProps={{
                                        htmlInput: {
                                            min:
                                                states.purchaseOrderFormik.values[EPurchaseOrderFields.PO_DATE] ||
                                                undefined,
                                        },
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography variant="label">Deal Done by*</Typography>
                                <MuiAutoComplete
                                    disablePortal={false}
                                    disableClearable
                                    size="large"
                                    placeholder="Select Name"
                                    options={states.dealDoneByUsersOptions}
                                    loading={states.isDealDoneByUsersFetching}
                                    value={states.purchaseOrderFormik.values[EPurchaseOrderFields.PO_DEAL_DONE_BY]}
                                    onChange={value =>
                                        states.purchaseOrderFormik.setFieldValue(
                                            EPurchaseOrderFields.PO_DEAL_DONE_BY,
                                            value
                                        )
                                    }
                                    error={functions.hasPurchaseOrderError(EPurchaseOrderFields.PO_DEAL_DONE_BY)}
                                />
                            </Box>
                        </Grid>

                        {/* PO Items Table */}
                        <Grid size={12}>
                            <PurchaseOrderItemsTable
                                items={states.purchaseOrderFormik.values.items}
                                onAddItem={functions.addNewItem}
                                onRemoveItem={functions.removeItem}
                                onUpdateItem={functions.updateItem}
                                skuOptions={states.skuOptions}
                                hasTableError={functions.hasItemsTableError}
                                getItemFieldError={functions.getItemFieldError}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography variant="label">Shipping Charges*</Typography>
                                <MuiTextField
                                    name={EPurchaseOrderFields.SHIPPING_CHARGES}
                                    placeholder="Enter Shipping Charges"
                                    size="large"
                                    type="number"
                                    value={states.purchaseOrderFormik.values[EPurchaseOrderFields.SHIPPING_CHARGES]}
                                    onChange={states.purchaseOrderFormik.handleChange}
                                    onBlur={states.purchaseOrderFormik.handleBlur}
                                    error={functions.hasPurchaseOrderError(EPurchaseOrderFields.SHIPPING_CHARGES)}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography variant="label" textTransform={"none"}>
                                    Total PO Value (w/o GST)*
                                </Typography>
                                <MuiTextField
                                    name={EPurchaseOrderFields.TOTAL_PO_VALUE}
                                    placeholder="Enter Total PO Value"
                                    size="large"
                                    type="number"
                                    value={states.purchaseOrderFormik.values[EPurchaseOrderFields.TOTAL_PO_VALUE]}
                                    disabled
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography variant="label">Remarks</Typography>
                                <MuiTextField
                                    name={EPurchaseOrderFields.REMARKS}
                                    placeholder="Enter Remarks"
                                    size="large"
                                    value={states.purchaseOrderFormik.values[EPurchaseOrderFields.REMARKS]}
                                    onChange={states.purchaseOrderFormik.handleChange}
                                    onBlur={states.purchaseOrderFormik.handleBlur}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Typography variant="label">Purchase Order*</Typography>
                                <FileUploaderV2
                                    onChange={functions.handlePOFileChange}
                                    error={functions.hasPurchaseOrderError(EPurchaseOrderFields.PO_FILE)}
                                />
                            </Box>
                        </Grid>
                        <Grid size={12}>
                            <Typography variant="label" textTransform={"none"}>
                                Payment terms (optional)
                            </Typography>
                            <PaymentTerms formik={states.purchaseOrderFormik} showEvidenceUpload={false} />
                        </Grid>
                    </Grid>
                )}
            </Box>
            <ProjectFormFooter
                handleCloseForm={props.handleCloseForm}
                isLoading={states.isAddingProjectTicket || states.isAddingPurchaseOrder}
                handleSubmit={() => states.formik.handleSubmit()}
            />
        </>
    )
}

export default ProjectBasicDetails
